import {
  cuoiThang,
  giaiMaKy,
  khoangNgayVn,
  laMaKy,
  luiNgay,
  MA_KY_LAN,
  MA_KY_MAC_DINH,
  maTuyChinh,
  nhanMaKy,
  thangLui,
  thangTron,
  type KyDaGiai,
  type MocChonKy,
  type MocDuLieu,
} from '@qlk/shared';

/**
 * Kỳ của **mọi trang có ô chọn kỳ**: Tổng quan, Kênh, Network, Hiệu suất và
 * trang chi tiết kênh.
 *
 * ⚠️ File này **không còn phép tính nào**: mã kỳ, khoảng ngày và kỳ trước nằm
 * hết ở `@qlk/shared/ky.ts` vì máy chủ cũng phải giải đúng mã đó (xem
 * `apps/api/src/lib/ky.ts`). Ở đây chỉ còn phần **giao diện** — danh sách mục
 * của ô chọn và tham số gọi API.
 *
 * Tám dạng `?ky=`: `7d` `28d` (mặc định) `90d` `365d` `all` `YYYY` `YYYY-MM`
 * `tu-YYYY-MM-DD_den-YYYY-MM-DD`.
 */

export {
  cuoiThang,
  laMaKy,
  luiNgay,
  MA_KY_LAN,
  MA_KY_MAC_DINH,
  maTuyChinh,
  nhanMaKy,
  thangTron,
  type MocChonKy,
  type MocDuLieu,
};

/** Kỳ đã giải của trang — xem `KyDaGiai` ở `@qlk/shared/ky.ts`. */
export type KyTrang = KyDaGiai;
export type { LoaiKy } from '@qlk/shared';

/** Giá trị giả của mục "Tuỳ chỉnh…": mở hộp chọn ngày, không phải một kỳ. */
export const MA_MO_TUY_CHINH = '__tuy-chinh__';

export interface MucChonKy {
  value: string;
  label: string;
  /** Nhóm trong ô chọn (`optgroup`) — vạch ngăn giữa các loại kỳ. */
  nhom: string;
}

/** Số năm hiện sẵn trong nhóm "Theo năm"; còn lại nằm sau "Năm khác…". */
export const SO_NAM_HIEN = 5;

/** Giá trị giả của mục "Năm khác…": mở hết danh sách năm, không phải một kỳ. */
export const MA_MO_NAM_KHAC = '__nam-khac__';

/**
 * Mục của ô chọn, xếp như YouTube Studio:
 *
 * ```
 * 7 ngày qua · 28 ngày qua · 90 ngày qua · 365 ngày qua · Toàn thời gian
 * ────────────────────────────
 * 2026 · 2025                      ← chỉ năm **có số liệu thật**
 * ────────────────────────────
 * Tháng 10/2026 · Tháng 9/2026 · Tháng 8/2026
 * ────────────────────────────
 * Tuỳ chỉnh…
 * ```
 *
 * Ba tháng chứ không mười hai: tháng thứ tư trở đi gần như không ai mở từ ô
 * này — cần tháng cũ thì đã có "Tuỳ chỉnh…", còn các trang tiền (Doanh thu,
 * Đối soát nhân sự) vốn có ô kỳ tháng riêng liệt kê đủ 12 tháng.
 *
 * Danh sách năm là `moc.cacNam` **y nguyên** từ máy chủ, không suy từ hai đầu
 * mốc. Một mục bấm vào ra bảng rỗng là một mục nói dối, nên năm trống — kể cả
 * năm nằm **giữa** hai đầu — không có mặt. Chưa có năm nào thì nhóm "Theo năm"
 * biến mất hẳn chứ không hiện một năm đoán.
 *
 * Quá `SO_NAM_HIEN` năm thì chỉ bày 5 năm gần nhất kèm mục "Năm khác…";
 * `moNamKhac` là trạng thái đã bấm mục đó (do `ui/ChonKy` giữ).
 */
export function cacMaKy(moc: MocChonKy, kyDangXem?: KyTrang, moNamKhac = false): MucChonKy[] {
  const thangCuoi = moc.ngayCuoi.slice(0, 7);
  const thang = thangCuoi === '' ? [] : [0, 1, 2].map((i) => thangLui(thangCuoi, i));

  const thuaNam = !moNamKhac && moc.cacNam.length > SO_NAM_HIEN;
  const nam = thuaNam ? moc.cacNam.slice(0, SO_NAM_HIEN) : moc.cacNam;

  const muc: MucChonKy[] = [
    ...Object.entries(MA_KY_LAN).map(([value, soNgay]) => ({
      value,
      label: `${String(soNgay)} ngày qua`,
      nhom: 'Khoảng gần đây',
    })),
    { value: 'all', label: 'Toàn thời gian', nhom: 'Khoảng gần đây' },
    ...nam.map((n) => ({ value: n, label: n, nhom: 'Theo năm' })),
    ...(thuaNam ? [{ value: MA_MO_NAM_KHAC, label: 'Năm khác…', nhom: 'Theo năm' }] : []),
    ...thang.map((t) => ({ value: t, label: nhanMaKy(t), nhom: 'Theo tháng' })),
  ];

  /*
   * Kỳ đang xem phải có mặt trong danh sách, nếu không `<select>` tự nhảy về
   * mục đầu và âm thầm đổi kỳ người dùng đang đọc. Hay gặp với kỳ tuỳ chỉnh và
   * với tháng cũ dán từ link của người khác.
   */
  if (kyDangXem && !muc.some((m) => m.value === kyDangXem.ma)) {
    muc.push({ value: kyDangXem.ma, label: kyDangXem.nhan, nhom: 'Khoảng tự chọn' });
  }
  muc.push({ value: MA_MO_TUY_CHINH, label: 'Tuỳ chỉnh…', nhom: 'Khoảng tự chọn' });

  return muc;
}

/**
 * Dựng kỳ của trang từ `?ky=` và mốc dữ liệu.
 *
 * `moc.ngayCuoi` rỗng (chưa tải xong `GET /analytics/moc`) thì `sanSang` bằng
 * `false` và chỗ gọi chưa bắn truy vấn nào — khoảng ngày đoán trước sẽ sai, mà
 * sai xong phải gọi lại lần hai.
 */
export function kyTuMa(ma: string, moc: MocDuLieu): KyTrang {
  return giaiMaKy(ma === '' ? MA_KY_MAC_DINH : ma, moc);
}

/**
 * Query string của kỳ cho API.
 *
 * Gửi **mã kỳ** chứ không phải hai ngày: chỉ mã mới nói được "kỳ trước" là
 * tháng trước hay 30 ngày trước, và chỉ mã mới cho máy chủ biết kỳ có trùng
 * trọn một tháng **đã chốt** hay không. Một hàm cho mọi chỗ gọi — hai nơi tự
 * ghép tham số là hai nơi có thể lệch nhau một ngày.
 */
export function thamSoKy(ky: KyTrang): string {
  return `ky=${encodeURIComponent(ky.ma)}`;
}

/** Khoá cache của kỳ — hai kỳ khác khoảng ngày là hai khoá khác nhau. */
export function khoaKy(ky: KyTrang): string {
  return `${ky.ma}|${ky.from}|${ky.to}`;
}

/**
 * Kỳ đã đủ dữ liệu để gọi API chưa.
 *
 * Kỳ tháng gọi được ngay (máy chủ tự suy khoảng từ `YYYY-MM`); kỳ khác phải
 * chờ mốc dữ liệu, vì khoảng đoán trước sẽ sai và sai xong phải gọi lần hai.
 */
export function goiDuocApi(ky: KyTrang): boolean {
  return ky.loai === 'thang' || ky.sanSang;
}

/**
 * Kỳ này nằm **ngoài** khoảng có số liệu hay không.
 *
 * Link cũ (`?ky=2017`) và link của người có phạm vi rộng hơn vẫn phải mở được
 * — trang không vỡ, chỉ hiện `—` kèm dòng "Không có số liệu trong kỳ này". So
 * bằng chuỗi `YYYY-MM-DD` được vì khoá ngày sắp theo thứ tự chữ.
 *
 * Chưa biết mốc (`sanSang` còn `false`) thì **không** kết luận: lúc đó mọi kỳ
 * đều trông như rỗng.
 */
export function kyNgoaiMocDuLieu(ky: KyTrang, moc: MocDuLieu): boolean {
  if (!ky.sanSang || moc.ngayCuoi === '' || moc.ngayDau === '') return false;
  return ky.to < moc.ngayDau || ky.from > moc.ngayCuoi;
}

/**
 * Nhãn của ô "so kỳ trước" — **một** câu cho mọi trang có ô chọn kỳ.
 *
 * Kỳ đã trọn (tháng hết tháng, 28 ngày qua) thì "so kỳ trước" là đủ: ai cũng
 * biết tháng 9 so với tháng 8. Kỳ **chưa trọn** thì phải ghi rõ, vì kỳ trước
 * của nó không phải cả tháng trước mà là cùng số ngày đầu tháng đó:
 * `▼13,8% so 01/09 – 26/09/2026`.
 *
 * `khoangTruoc` dành cho chỗ lấy kỳ trước từ **đáp án của máy chủ**
 * (`prevFrom`/`prevTo` của Hiệu suất và Phân tích) thay vì từ kỳ của trang.
 */
export function nhanSoKyTruoc(
  ky: Pick<KyTrang, 'tamTinh' | 'nhanKyTruoc'> | null | undefined,
  khoangTruoc?: { from: string; to: string },
): string {
  const khoang = khoangTruoc
    ? khoangNgayVn(khoangTruoc.from, khoangTruoc.to)
    : (ky?.nhanKyTruoc ?? '');

  return ky?.tamTinh === true && khoang !== '' ? `so ${khoang}` : 'so kỳ trước';
}

/** `2026` → `năm 2026` — dùng trong câu văn, khác nhãn ô chọn (chỉ `2026`). */
export function tenNam(nam: string): string {
  return `năm ${nam}`;
}
